// ─── Amber Path status TTL ──────────────────────────────────────────
// Shared TTL (seconds) for Amber Path status cache entries:
//   - `status:{correlationId}` / `status-owner:{correlationId}` (seeded by the
//     API route on 202), and
//   - the COMPLETED / FAILED status write from the worker.
//
// This MUST exceed the front-end polling budget (the page's MAX_POLL_DURATION_MS,
// currently 5 min) with headroom, so a slow-but-successful job's status is still
// readable when the front-end issues its final poll. The seed site (API) and the
// completion-write site (worker) must reference THIS SAME constant — a mismatch
// (e.g. a 600s seed against a 300s completion write) reintroduces the expiry race.
export const STATUS_TTL_SECONDS = 600;

// ─── Filter-options result cache TTL ────────────────────────────────
// Shared TTL (seconds) for the workspace-computed filter-option caches
// (`bq:overview-group-by-options:*`, `bq:my-brand-filters:*`). The worker WRITES
// under this TTL and the API's fast-path cache-hit check READS the same key, so
// both sites MUST reference this one constant — a duplicated literal (previously
// `30 * 60` in two files) is a silent-drift hazard.
export const OPTIONS_CACHE_TTL_SECONDS = 30 * 60;

// ─── Filter-options result cache key identities ─────────────────────
// The `{ domain, version }` pair behind every filter-option cache entry the workspace
// worker WRITES and the API and MCP READ. Three processes now build these keys, and
// the arguments differ per entry (clientKey vs tenantId vs industry), so what is shared
// here is the identity — the part that must be byte-identical — not the builder.
//
// Domains and versions are deliberately inconsistent (`v6` for competitor options,
// `v5` for creative hash, `v1` for the rest). API, workspace, and MCP must ship this
// identity together — bumping one side alone produces a cache that silently never hits.
//
// Held as plain data rather than `CacheKey` instances because `@nexus/contracts` is a
// leaf package — depending on `@nexus/backend-core` for the builder would invert the
// layering. Each consumer does `new CacheKey(domain, version)`.
export const FILTER_OPTIONS_CACHE_KEYS = {
    /** Distinct values for one Overview group-by dimension. Keyed on clientKey, groupBy, dateRange. */
    overviewGroupByOptions: { domain: 'bq:overview-group-by-options', version: 'v2' },
    /** My Brand platform/account/product tuples. Keyed on **tenantId**, not clientKey. */
    myBrandFilters: { domain: 'bq:my-brand-filters', version: 'v1' },
    /** Competitor brand options. Keyed on industry and source (`All` or sorted platform CSV). */
    competitorOptions: { domain: 'bq:competitor-options', version: 'v6' },
    /** Competitor brand options for an explicit YYYY-MM-DD window. Keyed on industry + dates + source. */
    competitorOptionsWindow: { domain: 'bq:competitor-options-window', version: 'v2' },
    /** Products within an industry. Keyed on industry. */
    industryProducts: { domain: 'bq:industry-products', version: 'v1' },
    /**
     * Distinct competitor ad-network sources. Keyed on a canonical industry pair:
     * comma-joined, case-preserved tokens, sorted case-insensitively, duplicates dropped.
     */
    competitorPlatforms: { domain: 'bq:competitor-platform-options', version: 'v1' },
    /** Creative-hash drill-in rows. Keyed on hash, industry, startDate, and endDate. */
    creativeHash: { domain: 'bq:creative-hash', version: 'v5' },
} as const;
